/**
 * Auto-mirrors every new assistant attachment into /documents/artifacts/.
 *
 * Rationale: the chip's manual "save to library" button (the ↗ icon
 * on TeacherAttachmentChip) wasn't discoverable enough — Charles
 * reported he couldn't find it. Mounting this no-render watcher
 * promotes every assistant-emitted PPTX / DOCX / image as soon as it
 * lands in the thread, so the teacher's mental model "AI outputs live
 * under Generated artifacts" works without any extra click.
 *
 * Idempotence: we key on the attachment's download URL and remember
 * which ones we've already shipped via a component-scoped ref-Set.
 * History replay won't re-upload — the URLs are stable across mount
 * and the ref persists for the conversation's lifetime. A fresh
 * conversation gets a fresh ref-Set because AssistantUiThread is
 * remounted by its parent on conversation change.
 *
 * Trade-off: every assistant attachment auto-mirrors, even small
 * inline images. If artifacts gets noisy with screenshots, we can
 * filter on mimeType or on minimum size later; for the Bosong demo
 * the win from "no missing files" beats the curation loss.
 */
import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useThread } from "@assistant-ui/react";

import { useAuth } from "@/auth/OidcProvider";
import { lookupRef } from "./refRegistry";
import { promoteAttachmentToLibrary } from "./promoteToLibrary";

type FilePart = { type: "file"; mimeType?: string; data?: string };
type ImagePart = { type: "image"; image?: string };
type TextPart = { type: "text"; text: string };
type Part = FilePart | ImagePart | TextPart;

function partUrl(p: Part): { url: string | null; mimeType: string } {
  if (p.type === "file") {
    return { url: p.data ?? null, mimeType: p.mimeType ?? "" };
  }
  if (p.type === "image") {
    return { url: p.image ?? null, mimeType: "image/*" };
  }
  return { url: null, mimeType: "" };
}

// Nova OS file-skill outputs (DOCX, PPTX, etc.) come back as markdown
// links embedded in the assistant text, not as structured file parts.
// The link path matches /uploads/<basename>.<ext>; isAuthFetchable in
// authDownload.ts already recognises this shape. We extract those
// URLs from text parts and treat each one as an extra attachment.
const UPLOADS_URL_RE = /(?:https?:\/\/[^\s)"'<>]*?)?\/uploads\/[^\s)"'<>]+\.[a-zA-Z0-9]{2,5}/g;
const MIRRORABLE_EXTS = new Set([
  "pptx", "ppt", "docx", "doc", "xlsx", "xls",
  "pdf", "txt", "md", "csv", "png", "jpg", "jpeg",
  // 2026-08-12 起 report_generation 的汇报类产物是带样式的 HTML
  // (Chart.js 报告等)——漏在白名单外导致产物栏"断更"在 8/11
  // (Jarod 2026-08-27)。mhtml 同理(前端已有预览器)。
  "html", "htm", "mhtml", "mht",
]);

function textUploadUrls(text: string): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  let m: RegExpExecArray | null;
  // Reset lastIndex for re-entry safety.
  UPLOADS_URL_RE.lastIndex = 0;
  while ((m = UPLOADS_URL_RE.exec(text)) !== null) {
    const raw = m[0];
    const ext = raw.split(".").pop()?.toLowerCase();
    if (!ext || !MIRRORABLE_EXTS.has(ext)) continue;
    if (seen.has(raw)) continue;
    seen.add(raw);
    out.push(raw);
  }
  return out;
}

function mimeFromExt(ext: string): string {
  switch (ext.toLowerCase()) {
    case "pptx": return "application/vnd.openxmlformats-officedocument.presentationml.presentation";
    case "ppt":  return "application/vnd.ms-powerpoint";
    case "docx": return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    case "doc":  return "application/msword";
    case "xlsx": return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    case "xls":  return "application/vnd.ms-excel";
    case "pdf":  return "application/pdf";
    case "txt":  return "text/plain";
    case "md":   return "text/markdown";
    case "csv":  return "text/csv";
    case "png":  return "image/png";
    case "jpg":  case "jpeg": return "image/jpeg";
    case "html": case "htm": return "text/html";
    case "mhtml": case "mht": return "message/rfc822";
    default:     return "application/octet-stream";
  }
}

function filenameFromUrl(url: string): string {
  try {
    const u = url.startsWith("http")
      ? new URL(url)
      : new URL(url, window.location.origin);
    const last = u.pathname.split("/").filter(Boolean).pop() ?? "download";
    return decodeURIComponent(last) || "download";
  } catch {
    return "download";
  }
}

export function ArtifactAutoSave(): null {
  const thread = useThread();
  const { authHeader } = useAuth();
  const qc = useQueryClient();
  const mirroredRef = useRef(new Set<string>());

  useEffect(() => {
    const messages = thread.messages ?? [];
    const candidates: { url: string; filename: string; mimeType: string }[] = [];

    for (const m of messages) {
      if (m.role !== "assistant") continue;
      const parts = (m.content ?? []) as Part[];
      for (const part of parts) {
        // Structured file/image parts — these are the well-behaved
        // attachments from the conversation-attachment adapter.
        if (part.type === "file" || part.type === "image") {
          const { url, mimeType: partMime } = partUrl(part);
          if (!url) continue;
          const ref = lookupRef(url);
          candidates.push({
            url,
            filename: ref?.name ?? filenameFromUrl(url),
            mimeType:
              ref?.mime_type ?? partMime ?? "application/octet-stream",
          });
          continue;
        }
        // Text parts — scan for embedded /uploads/<name>.<ext> URLs.
        // This is how Nova OS file-skill outputs (PPTX, DOCX) show up
        // when the orchestrator emits a markdown "Download …" link
        // rather than a structured attachment.
        if (part.type === "text" && part.text) {
          for (const url of textUploadUrls(part.text)) {
            const filename = filenameFromUrl(url);
            const ext = filename.split(".").pop() ?? "";
            candidates.push({
              url,
              filename,
              mimeType: mimeFromExt(ext),
            });
          }
        }
      }
    }

    for (const c of candidates) {
      if (mirroredRef.current.has(c.url)) continue;
      mirroredRef.current.add(c.url);
      promoteAttachmentToLibrary({
        downloadUrl: c.url,
        filename: c.filename,
        mimeType: c.mimeType,
        authHeader,
      })
        .then(() => {
          void qc.invalidateQueries({
            queryKey: ["documents-tree", "artifacts"],
          });
          void qc.invalidateQueries({ queryKey: ["documents-tree"] });
        })
        .catch(() => {
          // Silent on failure; drop from the seen-Set so a later
          // re-render can retry.
          mirroredRef.current.delete(c.url);
        });
    }
  }, [thread.messages, authHeader, qc]);

  return null;
}
